import { c as _c } from "react/compiler-runtime";
import type { CodePromptAttachment } from '@/data/code-sessions/types';
import type { CodeSkillSelection } from '@/lib/code-sessions/skills';
import type { RefObject } from 'react';
import { useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import type { ComposerAttachment } from './composer-attachments';
import type { SessionFileComment } from './file-comment-model';
import type { SkillDraft } from './prompt-skill-completion';
import { restoredAttachment } from './composer-attachments';
import { CODE_SKILL_SELECTION_LIMIT, codeSkillSelectionSchema } from '@/lib/code-sessions/skills';
import { safeSessionStorage } from '@/lib/safe-storage';
import { STORAGE_KEYS } from '@/lib/storage-keys';
import { useAuth } from '@/providers/auth-provider';

/** A sent prompt to put back in a session's follow-up composer. */
export type ComposerDraft = {
  text: string;
  skills?: readonly CodeSkillSelection[] | null;
  /** Saved files, sent again by ID; the server checks them again. */
  attachments: readonly CodePromptAttachment[];
};

/** What the composer holds: text with the skills bound to it, and the files attached. */
export type ComposerDraftState = SkillDraft & {
  attachments: readonly ComposerAttachment[];
};
export const EMPTY_COMPOSER_DRAFT: ComposerDraftState = {
  text: '',
  skills: [],
  attachments: []
};

/**
 * Whether a quote or file comment is still in the composer. The transcript shows
 * one a send has claimed in that message, and a rejected send puts it back.
 */
export function isInComposer({
  status
}: {
  status: SessionFileComment['status'];
}) {
  return status !== 'sending';
}
type ComposerDraftCommand = {
  type: 'load';
  draft: ComposerDraft;
  onRefused?: () => void;
  onLoaded?: (takeBack: () => void) => void;
} | {
  type: 'unload';
  onUnloaded?: (putBack: () => void) => void;
};

/**
 * One session's composer channel. Commands wait in `queue` until the
 * session's composer takes them, at once when it is mounted, or once it
 * mounts, as after navigating to a new fork.
 */
type DraftChannel = {
  queue: readonly ComposerDraftCommand[];
};
const channels = new Map<string, DraftChannel>();
const listeners = new Set<() => void>();
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function update(sessionId: string, change: (channel: DraftChannel) => DraftChannel) {
  const next = change(channels.get(sessionId) ?? {
    queue: []
  });
  if (next.queue.length === 0) {
    channels.delete(sessionId);
  } else {
    channels.set(sessionId, next);
  }
  listeners.forEach(listener => listener());
}
function send(sessionId: string, command: ComposerDraftCommand) {
  update(sessionId, channel => ({
    ...channel,
    queue: [...channel.queue, command]
  }));
}

/** The commands waiting for a session's composer, now taken by it. */
function takeCommands(sessionId: string): readonly ComposerDraftCommand[] {
  const queue = channels.get(sessionId)?.queue ?? [];
  if (queue.length > 0) {
    update(sessionId, channel => ({
      ...channel,
      queue: []
    }));
  }
  return queue;
}

/**
 * Edit from here: fill the composer with the prompt, below anything already
 * typed, and focus it. A composer that is not mounted yet, such as a new
 * fork's, takes it once it mounts. `onLoaded` runs once the prompt is in, with
 * a way to take it back out that works while the composer still holds it as
 * it came in.
 */
export function loadComposerDraft(sessionId: string, draft: ComposerDraft, options?: {
  onRefused?: () => void;
  onLoaded?: (takeBack: () => void) => void;
}) {
  send(sessionId, {
    type: 'load',
    draft,
    onRefused: options?.onRefused,
    onLoaded: options?.onLoaded
  });
}

/** Forget a queued load whose composer will never mount, as for a fork that failed to start. */
export function dropQueuedComposerDraft(sessionId: string) {
  update(sessionId, channel => ({
    ...channel,
    queue: []
  }));
}

/**
 * Take an unchanged loaded prompt back out, leaving what the user had typed before it.
 * `onUnloaded` runs only when the prompt came out, with a way to put it back that works while the
 * composer still holds what the unload left.
 */
export function unloadComposerDraft(sessionId: string, options?: {
  onUnloaded?: (putBack: () => void) => void;
}) {
  send(sessionId, {
    type: 'unload',
    onUnloaded: options?.onUnloaded
  });
}

/** The composer's files, then the restored prompt's that are not attached already. */
function appendAttachments(current: readonly ComposerAttachment[], restored: readonly CodePromptAttachment[]): ComposerAttachment[] {
  const attached = new Set(current.map(attachment => attachment.key));
  return [...current, ...restored.map(restoredAttachment).filter(attachment => !attached.has(attachment.key))];
}

/**
 * T3's refill: text already in the composer stays above the restored prompt.
 * Skills stay bound to their tokens, which move with the text. Files join the
 * ones already attached, also for a prompt that was only file comments or
 * quotes and so has no text of its own.
 * Returns null when the prompt cannot be restored within the skill selection limit.
 */
export function appendComposerDraft(current: ComposerDraftState, draft: ComposerDraft): ComposerDraftState | null {
  if (draft.text.length === 0) {
    const skills = (draft.skills ?? []).filter(skill => !skill.mention);
    if (current.skills.length + skills.length > CODE_SKILL_SELECTION_LIMIT) {
      return null;
    }
    const attachments = appendAttachments(current.attachments, draft.attachments);
    return skills.length === 0 && attachments.length === current.attachments.length ? current : {
      ...current,
      skills: [...current.skills, ...skills],
      attachments
    };
  }
  const keep = current.text.trim().length > 0;
  const offset = keep ? current.text.length + 2 : 0;
  const text = keep ? `${current.text}\n\n${draft.text}` : draft.text;
  const restored = (draft.skills ?? []).flatMap(skill => {
    const mention = skill.mention;
    // ID-only selections need no token; bound selections must still match the restored text.
    if (!mention) {
      return [skill];
    }
    const moved = {
      ...mention,
      start: mention.start + offset,
      end: mention.end + offset
    };
    return text.slice(moved.start, moved.end) === mention.text ? [{
      ...skill,
      mention: moved
    }] : [];
  });
  const skills = [...(keep ? current.skills : []), ...restored];
  if (skills.length > CODE_SKILL_SELECTION_LIMIT) {
    return null;
  }
  return {
    text,
    skills,
    attachments: appendAttachments(current.attachments, draft.attachments)
  };
}
type LoadedDraft = {
  before: ComposerDraftState;
  after: ComposerDraftState;
};
function sameAttachments(a: readonly ComposerAttachment[], b: readonly ComposerAttachment[]): boolean {
  return a.length === b.length && a.every((attachment, index) => attachment.key === b[index]?.key);
}
function sameDraft(a: ComposerDraftState, b: ComposerDraftState): boolean {
  return a.text === b.text && JSON.stringify(a.skills) === JSON.stringify(b.skills) && sameAttachments(a.attachments, b.attachments);
}

/**
 * Apply load/unload commands to a follow-up composer's draft. A restored
 * prompt counts as loaded until the user changes it; focus waits while the
 * composer is inert.
 */
export function useComposerDraftCommands(input: {
  sessionId: string | undefined;
  draft: ComposerDraftState;
  replaceDraft: (draft: ComposerDraftState) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  blocked: boolean;
}) {
  const {
    sessionId,
    draft,
    blocked,
    textareaRef
  } = input;
  const replaceRef = useRef(input.replaceDraft);
  replaceRef.current = input.replaceDraft;
  // Also written on replace, so a second command before the next render sees it.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const loadedRef = useRef<LoadedDraft | null>(null);
  const focusRef = useRef(false);

  /** Replace the draft now, so a command run right after sees it before the next render. */
  const writeDraft = useCallback((next: ComposerDraftState) => {
    draftRef.current = next;
    replaceRef.current(next);
  }, []);
  useEffect(() => {
    if (sessionId === undefined) {
      return;
    }
    const handle = (command: ComposerDraftCommand) => {
      const current = draftRef.current;
      const loaded = loadedRef.current;
      const holdsLoaded = loaded !== null && sameDraft(current, loaded.after);
      if (command.type === 'unload') {
        loadedRef.current = null;
        focusRef.current = false;
        if (holdsLoaded) {
          writeDraft(loaded.before);
          command.onUnloaded?.(() => {
            // Only into a composer still as the unload left it, so it never adds a second copy.
            if (sameDraft(draftRef.current, loaded.before)) {
              loadedRef.current = loaded;
              writeDraft(loaded.after);
            }
          });
        }
        return;
      }
      const before = holdsLoaded ? loaded.before : current;
      const after = appendComposerDraft(before, command.draft);
      if (after === null) {
        if (command.onRefused) {
          command.onRefused();
        } else {
          toast.error('Could not restore prompt', {
            description: `A prompt can have up to ${CODE_SKILL_SELECTION_LIMIT} skills. Remove a skill and try again.`
          });
        }
        return;
      }
      loadedRef.current = {
        before,
        after
      };
      focusRef.current = true;
      writeDraft(after);
      command.onLoaded?.(() => {
        // Only out of a composer still as this load left it, so it never drops text typed since.
        if (sameDraft(draftRef.current, after)) {
          loadedRef.current = loaded;
          focusRef.current = false;
          writeDraft(current);
        }
      });
    };
    const drain = () => takeCommands(sessionId).forEach(handle);
    drain();
    return subscribe(drain);
  }, [sessionId, writeDraft]);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!focusRef.current || blocked || !textarea) {
      return;
    }
    focusRef.current = false;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [blocked, draft, textareaRef]);
  return {
    writeDraft,
    /** The composer sent or cleared its draft; nothing is loaded any more. */
    releaseLoadedDraft: () => {
      const loaded_0 = loadedRef.current;
      loadedRef.current = null;
      focusRef.current = false;
      return () => {
        loadedRef.current ??= loaded_0;
      };
    }
  };
}
const keptDraftSchema = z.object({
  text: z.string().min(1),
  skills: z.array(codeSkillSelectionSchema)
});
function readKeptDraft(key: string): SkillDraft | null {
  const raw = safeSessionStorage.getItem(key);
  if (raw === null) {
    return null;
  }
  try {
    const kept = keptDraftSchema.safeParse(JSON.parse(raw));
    return kept.success ? kept.data : null;
  } catch {
    // A draft this tab cannot read is dropped, not restored.
    return null;
  }
}

/**
 * Keep the composer's text, with the skills bound to it, through a reload of
 * the tab, as the file comments and quotes sent with it are kept. Files are
 * not kept: the uploads the composer tracks end with the page.
 */
export function useKeptComposerDraft(input) {
  const $ = _c(17);
  const {
    draft,
    keep
  } = input;
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== input.session?.id || $[1] !== user) {
    t0 = user ? STORAGE_KEYS.CODE_COMPOSER_DRAFT(user.id, input.session?.id ?? "new") : null;
    $[0] = input.session?.id;
    $[1] = user;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const key = t0;
  let t1;
  if ($[3] !== draft || $[4] !== input) {
    t1 = kept => {
      if (draft.text.length === 0) {
        input.writeDraft({
          ...draft,
          ...kept
        });
      }
    };
    $[3] = draft;
    $[4] = input;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const restore = useEffectEvent(t1);
  let t2;
  if ($[6] !== key || $[7] !== restore) {
    t2 = () => {
      const kept_0 = key === null ? null : readKeptDraft(key);
      if (kept_0) {
        restore(kept_0);
      }
    };
    $[6] = key;
    $[7] = restore;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== key) {
    t3 = [key];
    $[9] = key;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useLayoutEffect(t2, t3);
  const {
    text,
    skills
  } = draft;
  let t4;
  let t5;
  if ($[11] !== keep || $[12] !== key || $[13] !== skills || $[14] !== text) {
    t4 = () => {
      if (key === null) {
        return;
      }
      if (keep && text.trim().length > 0) {
        safeSessionStorage.setItem(key, JSON.stringify({
          text,
          skills
        }));
      } else {
        safeSessionStorage.removeItem(key);
      }
    };
    t5 = [key, keep, text, skills];
    $[11] = keep;
    $[12] = key;
    $[13] = skills;
    $[14] = text;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
}